import {
  AdjustmentsHorizontalIcon,
  CheckIcon,
  ClockIcon,
  EllipsisVerticalIcon,
  Squares2X2Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  ChevronRightIcon,
  ExclamationTriangleIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/solid';
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router';

import { useTheme } from '@/app/ThemeContext';
import { AppRouteBody } from '@/components/AppRouteBody';
import { ConditionalRender } from '@/components/ConditionalRender';
import { Dropdown } from '@/components/Dropdown';
import { Entitled } from '@/components/Entitled';
import { Values } from '@/components/form/Inputs';
import { IntegrationErrorBoundary } from '@/components/IntegrationErrorBoundary';
import { Container } from '@/components/legacy-ui';
import { SearchBar } from '@/components/legacy-ui/SearchBar';
import {
  createAllEmptyConfig,
  createFilteredEmptyConfig,
  NO_DATA_ICON_CLASS,
} from '@/components/legacy-ui/utils/createNoDataConfig';
import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { Row } from '@/components/Row';
import { Table } from '@/components/table/Table';
import { CellAlignment, Column } from '@/components/table/types';
import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';
import { useModifyAccount } from '@/hooks/useAccounts';
import { AnalyticsEvent, useAnalytics } from '@/hooks/useAnalytics';
import { useEntitlements } from '@/hooks/useEntitlements';
import {
  AllIntegrations,
  generateSeverityOptions,
  useIntegration,
} from '@/hooks/useIntegration';
import { AccountWithType } from '@/hooks/useIntegration';
import { useIntegrationSettings } from '@/hooks/useIntegrationSettings';
import { useJobsStatus } from '@/hooks/useJobs';
import { useMy } from '@/hooks/useMy';
import { useTableRemountOnTransition } from '@/hooks/useTableRemountOnTransition';
import { ValidateIntegrationErrorResponse } from '@/hooks/useValidateIntegration';
import {
  computeHealthStatus,
  HealthStatus,
} from '@/sections/integrationHealth';
import SetupModal, { PermissionRow } from '@/sections/SetupModal';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import {
  AccountMetadata,
  Integration,
  JiraTemplate,
  JiraTransitionConfig,
} from '@/types';
import { Account } from '@/types';
import { cn } from '@/utils/classname';

interface IntegrationTableRow {
  id: string;
  health: HealthStatus;
  failureMessage?: string;
  surface: string;
  displayName?: string;
  pulledName?: string;
  identifier: string;
  description: string;
  config: unknown;
  member: string;
  originalAccount: Account;
  edit: boolean;
}

const Integrations = ({ customerEmail }: { customerEmail?: string }) => {
  const { canManageIntegrations } = useEntitlements();
  const { track } = useAnalytics();
  const { drawerState } = useGlobalState();
  const { isPraetorianUser, isDemoOrg } = useAuth();
  const showHealthFailures = isPraetorianUser && !isDemoOrg;
  const [search, setSearch] = useState('');
  const [setupIntegration, setSetupIntegration] = useState<AccountWithType>();
  const [formData, setFormData] = useState<
    Record<string, string | JiraTransitionConfig>
  >({});
  const [confirmDisconnect, setConfirmDisconnect] =
    useState<IntegrationTableRow | null>(null);
  const [editIntegration, setEditIntegration] = useState<AccountWithType>();
  const { currentTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const ignoredIntegrations = useMemo(
    () => [
      // Email and webhooks are managed from Settings > Notifications
      Integration.email,
      Integration.webhook,
      Integration.hook,
      // Identity Access Management
      Integration.entraid,
      Integration.googlesso,
    ],
    []
  );

  const {
    data: { connectedIntegrations, requiresSetupIntegrations },
    status: integrationStatus,
  } = useIntegration({
    email: customerEmail,
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes
  });
  const { invalidate: invalidateAccount } = useMy(
    { resource: 'account' },
    { enabled: false, email: customerEmail }
  );

  const { mutate: unlink } = useModifyAccount('unlink', customerEmail);
  const { mutateAsync: link } = useModifyAccount('link', customerEmail);

  // Job key format must match useIntegration.tsx jobKeys construction (line 2954).
  // Duplicated here because this map uses integration.key as keys
  // while useIntegration uses member#value. See useIntegration.tsx:2954.
  const { data: jobStatuses, status: jobStatusQueryStatus } = useJobsStatus(
    connectedIntegrations.reduce(
      (acc, integration) => {
        // Amazon keys omit trailing '#' because AWS account IDs are always
        // 12-digit numbers, making prefix collisions impossible.
        acc[integration.key] =
          `#job#${integration.member}#${integration.value}${integration.member === 'amazon' ? '' : '#'}`;
        return acc;
      },
      {} as Record<string, string>
    ),
    { refetchInterval: 5000, email: customerEmail }
  );

  // Watch for callback from GitHub App auth flow
  useEffect(() => {
    const handleGithubRedirect = async () => {
      const params = new URLSearchParams(location.search);
      const id = params.get('id');
      const pat = params.get('pat');
      const installation_id = params.get('installation_id');
      const value = params.get('value');

      if (id === 'github' && value && (pat || installation_id)) {
        const config = { username: 'github' } as Record<string, string>;
        if (pat) config['pat'] = pat;
        if (installation_id) config['installation_id'] = installation_id;

        try {
          await link({
            username: 'github',
            value: decodeURIComponent(value),
            config,
          });
        } catch (err) {
          console.error('Failed to create GitHub integration', err);
        } finally {
          params.delete('id');
          params.delete('pat');
          params.delete('value');
          navigate({ search: params.toString() }, { replace: true });
        }
      }
    };
    handleGithubRedirect();
  }, [link, location.search, navigate]);

  const getHealthIndicator = (
    status: HealthStatus,
    failureMessage?: string
  ) => {
    // Non-Praetorian users and demo org should not see failure states
    const effectiveStatus =
      !showHealthFailures &&
      (status === 'failure' || status === 'connected_with_errors')
        ? 'connected'
        : status;

    switch (effectiveStatus) {
      case 'connected':
        return (
          <Tooltip title="Connected and working">
            <CheckIcon
              data-testid="integration-health-connected"
              className="size-5 text-green-500"
            />
          </Tooltip>
        );
      case 'requiresSetup':
        return (
          <Tooltip title="Setup">
            <AdjustmentsHorizontalIcon className="size-5 text-yellow-500" />
          </Tooltip>
        );
      case 'no_jobs_found':
        return (
          <Tooltip title="Awaiting first job run">
            <ClockIcon className="text-chariot-text-secondary size-5" />
          </Tooltip>
        );
      case 'waitlisted':
        return (
          <Tooltip title="Waitlisted">
            <ClockIcon className="text-chariot-text-secondary size-5" />
          </Tooltip>
        );
      case 'failure':
        return (
          <Tooltip title={failureMessage || 'Connection failing'}>
            <XMarkIcon className="size-5 text-red-500" />
          </Tooltip>
        );
      case 'connected_with_errors':
        return (
          <Tooltip title={failureMessage || 'Some recent jobs have errors'}>
            <ExclamationTriangleIcon className="size-5 text-yellow-500" />
          </Tooltip>
        );
    }
  };

  const tableData = useMemo(() => {
    const demoIntegrations: typeof connectedIntegrations = isDemoOrg
      ? [
          {
            username: 'crowdstrike',
            key: 'crowdstrike#152f3b895bd6456b9eb5ac1aeda687b6',
            name: 'CrowdStrike Falcon',
            member: 'crowdstrike',
            value: '152f3b895bd6456b9eb5ac1aeda687b6',
            displayName: 'CrowdStrike Falcon',
            config: {},
            updated: '2024-01-01T00:00:00.000Z',
          },
        ]
      : [];

    const allIntegrations = [
      ...connectedIntegrations,
      ...demoIntegrations,
      ...requiresSetupIntegrations,
    ]
      .filter(
        integration =>
          !ignoredIntegrations.includes(integration.member as Integration)
      )
      .map(integration => {
        const jobStatus = jobStatuses?.[integration.key];
        const healthStatus = computeHealthStatus(integration.value, jobStatus);
        const isDemoCrowdstrike =
          isDemoOrg && integration.member === 'crowdstrike' && !jobStatus;

        return {
          id: integration.key,
          health: isDemoCrowdstrike ? 'connected' : healthStatus,
          failureMessage: showHealthFailures ? jobStatus?.comment : undefined,
          surface: integration.displayName,
          displayName: integration.settings?.displayName,
          pulledName: integration.settings?.pulledName,
          identifier:
            integration.value === 'setup'
              ? 'Requires Setup'
              : integration.value === 'waitlisted'
                ? 'Waitlisted'
                : integration.value || '[Redacted]',
          config: integration.config,
          member: integration.member,
          originalAccount: integration,
          edit: isDemoCrowdstrike
            ? false
            : AllIntegrations[
                integration.member as keyof typeof AllIntegrations
              ]?.edit,
        };
      })
      .sort((a, b) => {
        const nameCompare = (a.surface ?? '').localeCompare(b.surface ?? '');
        if (nameCompare !== 0) return nameCompare;
        return a.id.localeCompare(b.id);
      });

    if (!search) return allIntegrations as IntegrationTableRow[];

    return allIntegrations.filter(row =>
      [row.surface, row.identifier, row.displayName, row.pulledName].some(
        field => field?.toLowerCase().includes(search.toLowerCase())
      )
    ) as IntegrationTableRow[];
  }, [
    connectedIntegrations,
    requiresSetupIntegrations,
    jobStatuses,
    search,
    ignoredIntegrations,
    showHealthFailures,
    isDemoOrg,
  ]);

  // Calculate isFilteredEmpty: true when status is success but data is empty after filtering
  // This indicates search returned no results (not a loading/error state)
  const isFilteredEmpty =
    integrationStatus === 'success' && tableData.length === 0;

  // Calculate isAllEmpty: true when no integrations exist at all (first-time user)
  // Check both connected and requires-setup arrays (excluding ignored integrations)
  const totalIntegrations = [
    ...connectedIntegrations,
    ...requiresSetupIntegrations,
  ].filter(
    integration =>
      !ignoredIntegrations.includes(integration.member as Integration)
  ).length;
  const isAllEmpty =
    integrationStatus === 'success' && totalIntegrations === 0 && !isDemoOrg;

  // Detect initial loading state to prevent table UI flicker
  const isInitialLoading =
    integrationStatus === 'pending' ||
    (connectedIntegrations.length > 0 &&
      jobStatusQueryStatus !== 'success' &&
      jobStatusQueryStatus !== 'error');

  // Force table remount on empty → data transition to fix rendering bug
  // where clearing a search with no results leaves table stuck showing "No integrations found"
  const tableKey = useTableRemountOnTransition(
    isFilteredEmpty,
    tableData.length
  );

  const handleCloseSetupModal = () => {
    setSetupIntegration(undefined);
    setFormData({});
  };

  const columns: Column<IntegrationTableRow>[] = [
    {
      id: 'health',
      label: 'Health',
      fixedWidth: 100,
      cell: row => getHealthIndicator(row.health, row.failureMessage),
      align: 'center',
    },
    {
      id: 'surface',
      label: 'Integration',
      fixedWidth: 300,
      cell: row => (
        <div className="flex items-center gap-2">
          <img
            src={`/icons/${currentTheme}/${
              AllIntegrations[row.member as keyof typeof AllIntegrations]?.logo
            }`}
            alt={row.surface}
            className="size-5"
          />
          <span className="font-medium">{row.surface}</span>
        </div>
      ),
    },
    {
      id: 'identifier',
      label: 'Identifier',
      cell: row => {
        if (row.identifier === 'Setup') {
          return (
            <button
              className="text-chariot-brand-primary font-bold underline"
              onClick={() =>
                setSetupIntegration({
                  ...row.originalAccount,
                  type: row.surface,
                } as AccountWithType)
              }
            >
              Requires Setup
            </button>
          );
        }

        if (row.identifier === 'Waitlisted') {
          return (
            <div className="text-chariot-text-secondary italic">
              Coming Soon
            </div>
          );
        }

        const friendlyName = row.displayName || row.pulledName;

        return (
          <Tooltip title={row.identifier} tooltipClassName="max-w-sm break-all">
            <div className="max-w-xs">
              {friendlyName && (
                <div className="truncate font-medium">{friendlyName}</div>
              )}
              <div
                className={cn(
                  'truncate',
                  friendlyName && 'text-chariot-text-secondary text-xs'
                )}
              >
                {row.identifier}
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    ...(customerEmail || !canManageIntegrations
      ? []
      : [
          {
            id: 'config' as keyof IntegrationTableRow,
            label: 'Actions',
            fixedWidth: 150,
            align: 'center' as CellAlignment,
            cell: (row: IntegrationTableRow) => {
              const isDemoEntry =
                isDemoOrg &&
                row.id === 'crowdstrike#152f3b895bd6456b9eb5ac1aeda687b6';
              if (isDemoEntry) {
                return (
                  <div className="flex items-center justify-center">
                    <div className="w-full flex-1" />
                    <Dropdown
                      onClick={event => {
                        event.stopPropagation();
                      }}
                      startIcon={<EllipsisVerticalIcon className="size-6" />}
                      styleType="none"
                      menu={{
                        items: [
                          {
                            label: 'Disconnect',
                            onClick: () => {},
                          },
                        ],
                      }}
                    />
                  </div>
                );
              }
              return (
                <div className="flex items-center justify-center">
                  {row.health === 'requiresSetup' ? (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSetupIntegration({
                          ...row.originalAccount,
                          type: row.surface,
                        } as AccountWithType);
                      }}
                      size="sm"
                      className="rounded-full"
                    >
                      <AdjustmentsHorizontalIcon className="size-4" />
                      Setup
                    </Button>
                  ) : (
                    <div className="w-full flex-1" />
                  )}
                  <Dropdown
                    onClick={event => {
                      event.stopPropagation();
                    }}
                    startIcon={<EllipsisVerticalIcon className="size-6" />}
                    styleType="none"
                    menu={{
                      items: [
                        ...(row?.edit
                          ? [
                              {
                                label: 'Edit',
                                onClick: () => {
                                  setEditIntegration({
                                    ...row.originalAccount,
                                    type: row.surface,
                                  } as AccountWithType);
                                },
                              },
                            ]
                          : []),
                        {
                          label: 'Disconnect',
                          onClick: () => setConfirmDisconnect(row),
                        },
                      ],
                    }}
                  />
                </div>
              );
            },
          },
        ]),
  ];

  const noDataIcon = (
    <Squares2X2Icon className={`${NO_DATA_ICON_CLASS} size-12`} />
  );

  const noDataConfig = isAllEmpty
    ? createAllEmptyConfig({
        entityName: 'No Integrations Connected Yet',
        icon: noDataIcon,
        description:
          'Connect your first integration to start monitoring your attack surface',
        onGetStarted:
          customerEmail || !canManageIntegrations
            ? undefined
            : () => drawerState.handleOpenIntegrationDrawer(undefined, ''),
      })
    : createFilteredEmptyConfig({
        entityName: 'integrations',
        icon: noDataIcon,
        hasActiveFilters: !!search,
        onClearFilters: () => setSearch(''),
      });

  return (
    <ConditionalRender
      condition={!customerEmail}
      conditionalWrapper={children => {
        return <AppRouteBody title="Integrations">{children}</AppRouteBody>;
      }}
    >
      {!customerEmail && !isAllEmpty && (
        <Row>
          <SearchBar
            value={search}
            onChange={setSearch}
            size="sm"
            className="h-fit w-64 shrink-0 pr-2"
          />
          <Entitled entitlement="manage_integrations">
            <Button
              onClick={() =>
                drawerState.handleOpenIntegrationDrawer(undefined, '')
              }
              size="sm"
              className="ml-auto px-4"
            >
              <PlusCircleIcon className="size-4" />
              Add Integration
            </Button>
          </Entitled>
        </Row>
      )}
      <div className="flex h-full flex-col overflow-hidden">
        {isInitialLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader isLoading type="spinner" />
          </div>
        ) : (
          <Table
            key={tableKey}
            name="integrations"
            tableClassName={
              !customerEmail && canManageIntegrations
                ? '[&_th]:bg-chariot-background-secondary [&_th:last-child]:sticky [&_th:last-child]:right-0 [&_th:last-child]:z-10 [&_th:last-child]:bg-chariot-background-secondary [&_td:last-child]:sticky [&_td:last-child]:right-0 [&_td:last-child]:z-10 [&_td:last-child]:bg-chariot-background-primary [&_td:last-child]:shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]'
                : undefined
            }
            columns={columns}
            data={tableData as unknown as IntegrationTableRow[]}
            status={integrationStatus}
            error={null}
            noData={noDataConfig}
            selection={{
              hide: true,
            }}
          />
        )}
      </div>

      {/* Drawers and Modals */}
      <div className="z-10">
        {Boolean(setupIntegration) && (
          <SetupModal
            isOpen={Boolean(setupIntegration)}
            onClose={handleCloseSetupModal}
            onComplete={handleCloseSetupModal}
            selectedIntegration={setupIntegration}
            formData={formData as Values}
          />
        )}

        {Boolean(editIntegration) && editIntegration?.member === 'amazon' && (
          <EditAWSModal
            integration={editIntegration}
            onClose={() => setEditIntegration(undefined)}
          />
        )}

        {Boolean(editIntegration) && editIntegration?.member === 'jira' && (
          <IntegrationErrorBoundary
            onReset={() => setEditIntegration(undefined)}
            onError={error => {
              console.error('JIRA Integration Modal crashed:', error);
            }}
          >
            <EditJiraModal
              value={editIntegration?.value ?? ''}
              setEditIntegration={setEditIntegration}
              onDisconnect={() => {
                setEditIntegration(undefined);
                const disconnectIntegration =
                  tableData.find(({ member }) => member === 'jira') ?? null;
                setConfirmDisconnect(disconnectIntegration);
              }}
            />
          </IntegrationErrorBoundary>
        )}

        <Modal
          open={Boolean(confirmDisconnect)}
          onClose={() => setConfirmDisconnect(null)}
          title="Are you sure you want to disconnect this account?"
          footer={{
            baseClassName: 'flex justify-end gap-3',
            text: 'Disconnect',
            onClick: async () => {
              if (confirmDisconnect) {
                await unlink({
                  config: confirmDisconnect.originalAccount
                    .config as AccountMetadata,
                  key: confirmDisconnect.id,
                  member: confirmDisconnect.member,
                  value: confirmDisconnect.originalAccount.value ?? '',
                  username: confirmDisconnect.member,
                });
                track(AnalyticsEvent.INTEGRATION_DISCONNECT, {
                  integration: confirmDisconnect.member,
                });
                await invalidateAccount();
                setConfirmDisconnect(null);
              }
            },
          }}
        >
          <div className="flex flex-col gap-4">
            <p className="text-chariot-text-secondary -mt-2 text-sm">
              This action will disconnect the below account.
            </p>
            <div className="bg-chariot-background-secondary flex flex-col gap-2 rounded-lg p-4">
              <div className="flex flex-row items-center gap-2 text-sm font-bold">
                <img
                  src={`/icons/${currentTheme}/${
                    AllIntegrations[
                      confirmDisconnect?.originalAccount
                        .member as keyof typeof AllIntegrations
                    ]?.logo
                  }`}
                  alt={confirmDisconnect?.originalAccount.name}
                  className="m-2 size-10"
                />
                <div className="flex flex-col gap-2">
                  <p>Integration</p>
                  <p>{confirmDisconnect?.surface}</p>
                </div>
              </div>
              <p>Identifier: {confirmDisconnect?.originalAccount.value}</p>
            </div>
          </div>
        </Modal>
      </div>
    </ConditionalRender>
  );
};

export const EditJiraModal = ({
  value,
  setEditIntegration,
  onDisconnect,
}: {
  value: string;
  setEditIntegration: Dispatch<SetStateAction<AccountWithType | undefined>>;
  onDisconnect: () => void;
}) => {
  const jiraConfig = AllIntegrations['jira'];

  // Mutations
  const { currentTheme } = useTheme();
  const {
    data,
    status: statusSettings,
    error,
    templatesError,
    onDisconnectTemplate,
  } = useIntegrationSettings('jira', value);
  const { accountStatus, isValidating } = statusSettings;
  const status = isValidating ? 'pending' : accountStatus;
  const templates = (data?.settings?.templates ?? []).map(template => {
    return template as unknown as JiraTemplate;
  });

  // Local States
  const [step, setStep] = useState<
    'listTemplates' | 'configuration' | 'permissions'
  >('listTemplates');
  const [formData, setFormData] = useState<
    Record<string, string | JiraTransitionConfig>
  >({});

  return (
    <>
      {step === 'listTemplates' && (
        <Modal
          open={true}
          onClose={() => setEditIntegration(undefined)}
          size="4xl"
          title={
            <div className="flex w-full flex-row justify-between">
              <p>JIRA Configuration</p>
              <img
                src={`/icons/${currentTheme}/${jiraConfig?.logo}`}
                alt={jiraConfig.name}
                className="size-6"
              />
            </div>
          }
          footer={{
            className: 'w-fit px-2',
            hideCancelButton: false,
            left: (
              // TODO: Move the text-color to the chariot-ui
              <Button
                className={cn(
                  'border-none bg-transparent p-0 underline hover:bg-transparent',
                  currentTheme === 'light' ? 'text-[#AC1212]' : 'text-[#FF7171]'
                )}
                onClick={onDisconnect}
                size="sm"
              >
                Disconnect Jira Integration
              </Button>
            ),
            text: 'Add Project',
            onClick: () => {
              setFormData({
                displayName: data?.settings?.displayName ?? '',
              });
              setStep('configuration');
            },
            disabled: status === 'pending',
          }}
        >
          <Container variant="emphasized">
            <Table
              name="jiraProjects"
              columns={[
                {
                  id: 'projectKey',
                  label: '',
                  fixedWidth: 30,
                  cell: row => {
                    if (!templatesError?.[row?.id]) {
                      return null;
                    }

                    return (
                      <InsufficientPermissionsTooltip
                        template={row}
                        templateError={templatesError[row.id]}
                        onDisconnectTemplate={onDisconnectTemplate}
                      />
                    );
                  },
                },
                {
                  id: 'projectKey',
                  label: 'Project Key',
                  fixedWidth: 150,
                },
                {
                  id: 'projectName',
                  label: 'Project Name',
                },
                {
                  id: 'issueType',
                  label: 'Issue Type',
                  fixedWidth: 100,
                },
                {
                  id: 'severities',
                  label: 'Auto Ticket Creation',
                  fixedWidth: 200,
                  cell: row => {
                    const options = generateSeverityOptions();
                    const autoCreate = row?.autoCreate === 'true';

                    const label = options.find(
                      option => option.value === row.severities
                    )?.label;
                    return autoCreate ? label : '-';
                  },
                },
              ]}
              rowActions={row => {
                if (!row) {
                  return {
                    menu: {
                      items: [],
                    },
                  };
                }

                const missingPermissions = templatesError?.[row?.id];

                return {
                  menu: {
                    items: [
                      ...(missingPermissions
                        ? []
                        : [
                            {
                              disabled: status === 'pending',
                              label: 'Edit',
                              onClick: () => {
                                // Cast row to Record with possible transitions field
                                const rowData = row as unknown as Record<
                                  string,
                                  string | JiraTransitionConfig
                                >;
                                setFormData({
                                  ...rowData,
                                  displayName:
                                    data?.settings?.displayName ?? '',
                                });
                                setStep('configuration');
                              },
                            },
                          ]),
                      {
                        disabled: status === 'pending',
                        label: 'Disconnect',
                        onClick: () => {
                          if (templates.length === 1) {
                            onDisconnect();
                          } else {
                            onDisconnectTemplate(row);
                          }
                        },
                      },
                    ],
                  },
                };
              }}
              loadingRowCount={4}
              selection={{
                hide: true,
              }}
              data={templates}
              status={accountStatus}
              error={error}
            />
          </Container>
        </Modal>
      )}
      {step === 'configuration' && (
        <SetupModal
          step="configuration"
          isOpen={Boolean(value)}
          onClose={() => setEditIntegration(undefined)}
          onComplete={() => setEditIntegration(undefined)}
          selectedIntegration={data}
          formData={formData as Values}
        />
      )}
    </>
  );
};

const EditAWSModal = ({
  integration,
  onClose,
}: {
  integration: AccountWithType;
  onClose: () => void;
}) => {
  const [displayName, setDisplayName] = useState(
    integration.settings?.displayName || ''
  );
  const { mutateAsync: update, status } = useModifyAccount('update');

  const handleSave = async () => {
    const existingSettings = integration.settings || {};
    const newSettings = {
      ...existingSettings,
      displayName: displayName.trim() || undefined,
    };

    await update({
      username: 'amazon',
      key: integration.key,
      value: integration.value,
      config: integration.config || {},
      settings: newSettings,
    });

    onClose();
  };

  return (
    <Modal
      open={true}
      onClose={onClose}
      title="Edit AWS Display Name"
      footer={{
        text: status === 'pending' ? 'Saving...' : 'Save',
        onClick: handleSave,
        disabled: status === 'pending',
      }}
    >
      <div className="flex flex-col gap-4">
        <p className="text-chariot-text-secondary text-sm">
          Set a friendly name for this AWS account. Leave empty to use the
          auto-detected name or account ID.
        </p>
        <div>
          <label className="mb-1 block text-sm font-medium">Display Name</label>
          <input
            type="text"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            placeholder={
              integration.settings?.pulledName || integration.value || ''
            }
            className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary w-full rounded-md border px-3 py-2 text-sm"
          />
        </div>
        {integration.value && (
          <p className="text-chariot-text-secondary text-xs">
            Account ID: {integration.value}
          </p>
        )}
      </div>
    </Modal>
  );
};

export const InsufficientPermissionsTooltip = ({
  templateError,
  template,
  onDisconnectTemplate,
}: {
  templateError?: NonNullable<
    ValidateIntegrationErrorResponse['templates']
  >[string];
  template: JiraTemplate;
  onDisconnectTemplate: (template: JiraTemplate) => void;
}) => {
  const permissions = AllIntegrations['jira']?.configuration?.permissions;
  return (
    <Tooltip
      tooltipClassName="bg-chariot-background-primary border border-chariot-stroke"
      title={
        <div className="">
          <h3 className="text-sm font-bold">Insufficient Permissions</h3>
          <p className="text-chariot-text-secondary text-xs">
            Unable to create JIRA tickets from Chariot.
          </p>

          <div className="mt-2 flex flex-col gap-1">
            {(templateError?.missingPermissions ?? []).map(
              missingPermission => (
                <PermissionRow
                  key={missingPermission}
                  name={permissions?.[missingPermission]?.name ?? ''}
                  description={
                    permissions?.[missingPermission]?.description ?? ''
                  }
                  permission={
                    permissions?.[missingPermission]?.permission ?? ''
                  }
                  havePermission={false}
                />
              )
            )}
          </div>

          <Button
            variant="outline"
            className="my-1 rounded-full"
            onClick={() => onDisconnectTemplate(template)}
            size="sm"
          >
            Disconnect Project
            <ChevronRightIcon className="size-4" />
          </Button>
        </div>
      }
    >
      <ExclamationTriangleIcon className="text-chariot-stroke-critical size-5" />
    </Tooltip>
  );
};

export default Integrations;
